import React, { Fragment } from 'react';
import { HoverCard, Stack, Table, Text } from '@mantine/core';

import { NOT_APPLICABLE } from 'constants/defaultValues';

import { formatToNumericDate, sortByCreatedAt } from 'util/date';
import useBroswerLanguage from 'util/hooks/useLanguage';
import {
  getDisplayForIsEMIADCP,
  getHistoryByOperation,
  getSampleHistoryRowData,
} from 'util/samplingHistory';
import { SamplingDateType, SamplingHistorySummaryType } from 'store/samplingHistory/types';

type SamplingHistoryExpandedPropsType = {
  sampledDate: SamplingDateType;
};

export const SamplingHistoryExpanded = ({ sampledDate }: SamplingHistoryExpandedPropsType) => {
  const language = useBroswerLanguage();
  const fieldsByOperation = getHistoryByOperation(sampledDate.sampled_fields);

  const getShippedDisplay = (field: SamplingHistorySummaryType) => {
    const shippingLabels = field.shipping_labels;

    if (!shippingLabels?.length) {
      return <Text inherit>{NOT_APPLICABLE}</Text>;
    }

    const labelByCreatedDate = sortByCreatedAt(shippingLabels).reduce(
      (dates, label) => {
        const date = formatToNumericDate(label.created_at);
        dates[date] = [...(dates[date] || []), label.tracking_number];
        return dates;
      },
      {} as { [date: string]: string[] },
    );
    return (
      <Stack gap="0">
        {Object.entries(labelByCreatedDate).map(([date, trackingNums]) => (
          <HoverCard key={date}>
            <HoverCard.Target>
              <Text inherit style={{ cursor: 'pointer' }}>
                {formatToNumericDate(date)}
              </Text>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Stack>
                {trackingNums.map((track) => (
                  <Text key={track} inherit>
                    {track}
                  </Text>
                ))}
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        ))}
      </Stack>
    );
  };

  return (
    <Table verticalSpacing="0" withRowBorders={false}>
      <Table.Tbody>
        {fieldsByOperation.map((operation) => {
          const {
            sampledArea: operationArea,
            pointSamples: operationPoints,
            zoneSamples: operationZones,
          } = getSampleHistoryRowData(operation.fields);
          return (
            <Fragment key={operation.id}>
              <Table.Tr style={{ border: 0 }}>
                <Table.Th w="20rem" miw="11rem">
                  <Text inherit>{operation.name}</Text>
                </Table.Th>
                <Table.Th w="14%" miw="5.5rem" ta="right">
                  <Text inherit>{operation.fields.length}</Text>
                </Table.Th>
                <Table.Th w="8%" miw="4.5rem" ta="right">
                  <Text inherit>{operationArea}</Text>
                </Table.Th>
                <Table.Th w="8%" miw="4.5rem" ta="right">
                  <Text inherit>{operationPoints}</Text>
                </Table.Th>
                <Table.Th w="8%" miw="4.5rem" ta="right">
                  <Text inherit>{operationZones}</Text>
                </Table.Th>
                <Table.Td w="40%" colSpan={2} />
                <Table.Th w="3rem" maw="3rem" miw="3rem" />
              </Table.Tr>
              {operation.fields.map((field, findex) => {
                const {
                  sampledArea: fieldArea,
                  pointSamples: fieldPoints,
                  zoneSamples: fieldZones,
                  isEMI,
                  isADCP,
                  arrivedSamples,
                } = getSampleHistoryRowData([field]);
                return (
                  <Table.Tr key={`${field.field_id}_${findex}`} style={{ border: 0 }}>
                    <Table.Td ta="right">
                      <Text inherit fs="italic">
                        {field.field_name}
                      </Text>
                    </Table.Td>
                    <Table.Td />
                    <Table.Td ta="right">
                      <Text inherit>{fieldArea}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text inherit>{fieldPoints}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text inherit>{fieldZones}</Text>
                    </Table.Td>
                    <Table.Td w="8%" ta="right">
                      <Text inherit>{getDisplayForIsEMIADCP(isEMI, language)}</Text>
                    </Table.Td>
                    <Table.Td w="8%" ta="right">
                      <Text inherit>{getDisplayForIsEMIADCP(isADCP, language)}</Text>
                    </Table.Td>
                    <Table.Td w="16%" ta="right">
                      {getShippedDisplay(field)}
                    </Table.Td>
                    <Table.Td w="8%" miw="4.5rem" ta="right">
                      {arrivedSamples}
                    </Table.Td>
                    <Table.Td w="3rem" maw="3rem" miw="3rem" />
                  </Table.Tr>
                );
              })}
              <Table.Tr h="xs" style={{ border: 0 }} />
            </Fragment>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};
